import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Form } from "react-final-form";
import { Box, Button, LinearProgress, Tooltip } from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackRounded from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRounded from "@mui/icons-material/ArrowForwardRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import arrayMutators from "final-form-arrays";
import getIn from "lodash/get";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import BrandLogo from "../branding/BrandLogo";
import BrandThemeProvider from "../branding/BrandThemeProvider";
import colors from "../../theme/colors";
import { selectActiveBrand } from "../../redux/reducers/brandReducer";
import { TOGGLE_PILL_SX } from "../../theme/formCardStyles";
import PublicFormProgressRail from "./PublicFormProgressRail";
import { APP_TOKENS, PUBLIC_FORM_FIELD_SX } from "./publicFormTokens";

/**
 * The footer's primary action (Continue / Submit), in brand blue.
 *
 * LENDMARQ DIVERGENCE: a bare `variant="contained"` renders dark navy here —
 * this app's theme overrides MuiButton rather than taking `primary.main`, which
 * is what tpo's `contained` picks up. Background and text color are set
 * together, per the repo convention: never override one without the other.
 */
const PRIMARY_ACTION_SX = {
  bgcolor: colors.action,
  color: colors.white,
  "&:hover": { bgcolor: colors.actionHover },
  "&.Mui-disabled": {
    bgcolor: alpha(colors.action, 0.38),
    color: colors.white,
  },
};

/**
 * Layout + step machine for the public (unauthenticated) forms.
 *
 * Not built on the dashboard's form components: this needs a step model they
 * don't have — revisitable history with a `furthestStep` ceiling, a review step
 * that can jump back to any section, per-step draft persistence, and a rail with
 * sublabels. The underlying form is still react-final-form + arrayMutators, so
 * every Rff* field works inside it unchanged.
 *
 * `steps` is [{ label, sublabel, title, description, Component }].
 */
const PublicFormShell = ({
  formTitle = "Loan Application",
  submitLabel = "Submit Application",
  steps,
  onSubmit,
  initialValues,
  initialStep = 0,
  loading,
  hideLogo,
  customLogoName,
  onValuesChange,
  draftBanner,
  devTools,
}) => {
  const [step, setStep] = useState(initialStep);
  const [furthestStep, setFurthestStep] = useState(initialStep);
  const [stepError, setStepError] = useState("");
  const scrollRef = useRef(null);

  const totalSteps = steps.length;
  const isLastStep = step === totalSteps - 1;
  const active = steps[step];
  const StepComponent = active.Component;

  // Every step change returns to the top — steps are long enough that landing
  // mid-page on the next one is disorienting.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [step]);

  const goTo = (next) => {
    const clamped = Math.max(0, Math.min(next, totalSteps - 1));
    setStepError("");
    setStep(clamped);
    setFurthestStep((prev) => Math.max(prev, clamped));
  };

  /**
   * Gate Continue on the current step's validity.
   *
   * Only the active step is mounted, so `getRegisteredFields()` is exactly the
   * current step's field set — which lets one generic check cover every step
   * without maintaining a step-to-fields map that would drift.
   */
  const invalidStepFields = (form) => {
    // Deduped: two <Field>s can share a name (a conditional re-registering the
    // same path), and counting the name twice would inflate the tally the
    // banner shows.
    const registered = [...new Set(form.getRegisteredFields())];

    // Touch everything so fields that were never visited render their errors
    // rather than silently failing the check.
    registered.forEach((name) => {
      form.focus(name);
      form.blur(name);
    });

    const { errors } = form.getState();
    return registered.filter((name) => Boolean(getIn(errors, name)));
  };

  const handleNext = (form, values) => {
    const invalid = invalidStepFields(form);
    if (invalid.length > 0) {
      // No scroll: the banner sits directly above Continue, which is where the
      // applicant just clicked, so it's already on screen.
      setStepError(
        invalid.length === 1
          ? "Please complete the highlighted field before continuing."
          : `Please complete the ${invalid.length} highlighted fields before continuing.`,
      );
      return;
    }
    setStepError("");
    onValuesChange?.(values, Math.min(step + 1, totalSteps - 1));
    goTo(step + 1);
  };

  /**
   * Only the active step is mounted, so final-form's own submit gate never sees
   * a step the applicant skipped by resuming a draft — and drafts deliberately
   * omit the SSN. A step's `submitCheck(values)` returns a message when it is
   * incomplete; the first one that fails sends the applicant back to it.
   */
  const handleFinalSubmit = (values) => {
    const gapIndex = steps.findIndex((s) => s.submitCheck?.(values));
    if (gapIndex !== -1) {
      goTo(gapIndex);
      setStepError(steps[gapIndex].submitCheck(values));
      return undefined;
    }
    return onSubmit(values);
  };

  return (
    <Form
      onSubmit={handleFinalSubmit}
      initialValues={initialValues}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, form, values }) => (
        // LENDMARQ DIVERGENCE: makes `primary.main` the brand blue for the whole
        // form. This app's palette is navy, so every accent the vendored tree
        // takes from `primary.main` rendered dark — see BrandThemeProvider.
        <BrandThemeProvider>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: "flex",
            minHeight: "100vh",
            background: APP_TOKENS.canvas,
          }}
        >
          <PublicFormProgressRail
            formTitle={formTitle}
            steps={steps}
            activeStep={step}
            furthestStep={furthestStep}
            onStepSelect={goTo}
            hideLogo={hideLogo}
            customLogoName={customLogoName}
          />

          <Flx column sx={{ flex: 1, minWidth: 0 }}>
            <MobileHeader
              step={step}
              totalSteps={totalSteps}
              label={active.label}
              hideLogo={hideLogo}
            />

            <Box
              ref={scrollRef}
              sx={{ flex: 1, overflowY: "auto", px: { xs: 2, md: 5 }, py: 4 }}
            >
              <Flx
                column
                gap={3}
                sx={{ maxWidth: APP_TOKENS.contentMaxWidth, mx: "auto" }}
              >
                {draftBanner}

                {/* The whole step is one sheet: header, sections, and their
                    dividers all live inside a single card. The field styles are
                    spread here so the restyle is scoped to this surface and
                    never leaks into the dashboard's forms. */}
                <Box
                  sx={{
                    background: APP_TOKENS.surface,
                    border: `1px solid ${APP_TOKENS.border}`,
                    borderRadius: "14px",
                    boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
                    p: { xs: 2.5, md: "44px 48px 40px" },
                    ...PUBLIC_FORM_FIELD_SX,
                    // Shared with Create Loan and Send-to-Borrower, so every
                    // toggle in the portal is the same pill. Without it these
                    // fall back to MUI's default segmented bar, which reads as a
                    // different control — most obviously on a group with long
                    // option labels.
                    ...TOGGLE_PILL_SX,
                  }}
                >
                  <Flx column gap={1} sx={{ mb: 4.5 }}>
                    <Txt
                      component="div"
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        textTransform: "uppercase",
                        color: "primary.main",
                      }}
                    >
                      {`Step ${step + 1} of ${totalSteps}`}
                    </Txt>
                    <Txt
                      component="h1"
                      sx={{
                        m: 0,
                        fontSize: 24,
                        fontWeight: 700,
                        letterSpacing: "-0.02em",
                        color: APP_TOKENS.ink,
                      }}
                    >
                      {active.title}
                    </Txt>
                    {active.description && (
                      <Txt
                        component="div"
                        sx={{ fontSize: 14, color: APP_TOKENS.inkMuted }}
                      >
                        {active.description}
                      </Txt>
                    )}
                  </Flx>

                  <StepComponent goToStep={goTo} values={values} />

                  <FooterNav
                    step={step}
                    isLastStep={isLastStep}
                    loading={loading}
                    stepError={stepError}
                    submitLabel={submitLabel}
                    // Both public forms sign into `signature`, and an unsigned
                    // submission is not a submission — the e-signature is the
                    // authorization, not a field alongside it.
                    awaitingSignature={isLastStep && !values?.signature}
                    onBack={() => goTo(step - 1)}
                    onNext={() => handleNext(form, values)}
                  />
                </Box>

                {/* Below the card, outside it: local-dev affordances only (see
                    DevPrefillBar). Kept out of the sheet so nothing here can be
                    mistaken for a step action or disturb the footer's rhythm. */}
                {devTools}
              </Flx>
            </Box>
          </Flx>
        </Box>
        </BrandThemeProvider>
      )}
    />
  );
};

const StepErrorBanner = ({ message }) => (
  <Flx
    centerY
    gap={1.5}
    sx={{
      border: `1px solid ${colors.danger}`,
      background: colors.dangerBg,
      borderRadius: APP_TOKENS.radius,
      px: 2.5,
      py: 1.75,
    }}
  >
    <ErrorOutlineRounded sx={{ color: colors.dangerFg, fontSize: 20 }} />
    <Txt component="div" sx={{ fontSize: 13.5, color: colors.dangerFg }}>
      {message}
    </Txt>
  </Flx>
);

/**
 * Compact progress header — the rail is hidden below md, so this stands in for
 * it and carries the same brand header colors.
 */
const MobileHeader = ({ step, totalSteps, label, hideLogo }) => {
  const { headerBg, headerFg } = useSelector(selectActiveBrand);

  return (
    <Flx
      column
      gap={1.5}
      sx={{
        display: { xs: "flex", md: "none" },
        px: 2,
        pt: 2,
        pb: 1.5,
        background: headerBg,
        borderBottom: `1px solid ${alpha(headerFg, 0.14)}`,
        position: "sticky",
        top: 0,
        zIndex: 2,
      }}
    >
      <Flx between centerY>
        {hideLogo ? (
          <Box />
        ) : (
          <BrandLogo height={26} maxWidth={140} color={headerFg} />
        )}
        <Txt
          component="div"
          sx={{ fontSize: 12, color: alpha(headerFg, 0.65) }}
        >
          {`Step ${step + 1} of ${totalSteps} · ${label}`}
        </Txt>
      </Flx>
      <LinearProgress
        variant="determinate"
        value={Math.round(((step + 1) / totalSteps) * 100)}
        sx={{
          height: 4,
          borderRadius: 99,
          background: alpha(headerFg, 0.18),
          "& .MuiLinearProgress-bar": {
            borderRadius: 99,
            backgroundColor: headerFg,
          },
        }}
      />
    </Flx>
  );
};

/**
 * Step navigation, sitting at the foot of the card behind its own rule — the
 * same divider treatment the sections use, so it reads as the last block of the
 * sheet rather than a separate bar.
 */
const FooterNav = ({
  step,
  isLastStep,
  loading,
  stepError,
  submitLabel,
  awaitingSignature = false,
  onBack,
  onNext,
}) => (
  <Box sx={{ mt: 5.5, pt: 4, borderTop: `1px solid ${APP_TOKENS.border}` }}>
    {/* Sits directly above the buttons: the applicant's attention is here when
        Continue rejects, and the fields it refers to are highlighted above. */}
    {stepError && (
      <Box sx={{ mb: 2.5 }}>
        <StepErrorBanner message={stepError} />
      </Box>
    )}
    <Flx between centerY gap={2} fullWidth>
      {/* variant="text" so the theme owns the color — never override a button's
          text color without also setting its background. */}
      <Button
        variant="text"
        startIcon={<ArrowBackRounded />}
        disabled={step === 0 || loading}
        onClick={onBack}
      >
        Back
      </Button>

      {isLastStep ? (
        // A disabled button with no explanation is a dead end — the tooltip
        // (and the span it needs, since a disabled button fires no events) says
        // what is missing.
        <Tooltip
          title={awaitingSignature ? "Adopt your signature above to submit" : ""}
          placement="top"
        >
          <span>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading || awaitingSignature}
              sx={{ ...PRIMARY_ACTION_SX, minWidth: 200 }}
            >
              {loading ? "Submitting…" : submitLabel}
            </Button>
          </span>
        </Tooltip>
      ) : (
        <Button
          type="button"
          variant="contained"
          size="large"
          endIcon={<ArrowForwardRounded />}
          onClick={onNext}
          sx={{ ...PRIMARY_ACTION_SX, minWidth: 160 }}
        >
          Continue
        </Button>
      )}
    </Flx>
  </Box>
);

export default PublicFormShell;
